/**
 * Shared line row — renders a single line with:
 *   - Selection highlight (click to select)
 *   - Keyword highlights (<mark> elements)
 *   - Spotlight highlight (full-line background)
 *
 * Each viewer passes its own `renderContent` to customize how
 * the line text is displayed (e.g., speaker prefix for transcripts,
 * header styling for emails).
 */

import React from 'react';
import {HighlightRange} from './types';

// ── Highlighted text rendering ───────────────────────────────────────────

export function renderHighlightedText(
    text: string,
    highlights: HighlightRange[],
): React.ReactNode {
    if (!highlights.length) return text;

    const parts: React.ReactNode[] = [];
    let cursor = 0;
    for (const h of highlights) {
        if (h.start > cursor) {
            parts.push(text.slice(cursor, h.start));
        }
        parts.push(
            <mark
                key={`${h.start}-${h.end}`}
                className={h.isSpotlight ? 'spotlight-match' : undefined}
                style={{backgroundColor: h.color, opacity: 0.6, borderRadius: 2}}
            >
                {text.slice(h.start, h.end)}
            </mark>
        );
        cursor = h.end;
    }
    if (cursor < text.length) {
        parts.push(text.slice(cursor));
    }
    return <>{parts}</>;
}

// ── Line row component ───────────────────────────────────────────────────

export interface LineRowProps {
    index: number;
    text: string;
    isSelected: boolean;
    isSpotlighted: boolean;
    spotlightClass: string;
    highlights: HighlightRange[];
    onClick: (index: number, e: React.MouseEvent) => void;
    /** Optional custom content renderer. Receives text + highlights.
     *  If not provided, renders text with highlights applied. */
    renderContent?: (text: string, highlights: HighlightRange[]) => React.ReactNode;
    /** Extra CSS classes for the line row */
    className?: string;
    /** Extra inline styles */
    style?: React.CSSProperties;
    /** Optional left gutter content (line number, page number, etc.) */
    gutter?: React.ReactNode;
    /** Optional annotation displayed after the gutter (e.g., resolved speaker name) */
    annotation?: React.ReactNode;
    /** If true, render as an empty spacer line */
    isEmpty?: boolean;
    /** 1-based page number — stored as data-page for scroll sync */
    pageNumber?: number;
    /** Char offset in the original document body — stored as data-body-offset for split-view click-to-sync */
    bodyOffset?: number;
}

const LineRow: React.FC<LineRowProps> = React.memo(({
    index,
    text,
    isSelected,
    isSpotlighted,
    spotlightClass,
    highlights,
    onClick,
    renderContent,
    className,
    style,
    gutter,
    isEmpty,
    pageNumber,
    bodyOffset,
}) => {
    if (isEmpty) {
        return (
            <div
                className={`line-row line-row--empty ${className ?? ''}`}
                data-line-index={index}
                data-page={pageNumber}
            >
                {gutter !== undefined && <span className="line-row__gutter">{gutter}</span>}
                <span className="line-row__content">&nbsp;</span>
            </div>
        );
    }

    const content = renderContent
        ? renderContent(text, highlights)
        : renderHighlightedText(text, highlights);

    return (
        <div
            className={[
                'line-row',
                isSelected ? 'line-row--selected' : '',
                isSpotlighted ? spotlightClass : '',
                className ?? '',
            ].filter(Boolean).join(' ')}
            style={{
                ...(isSpotlighted ? {backgroundColor: '#fef3c7'} : {}),
                ...style,
            }}
            data-line-index={index}
            data-page={pageNumber}
            data-content={text}
            data-body-offset={bodyOffset}
            onClick={(e) => onClick(index, e)}
        >
            {gutter !== undefined && <span className="line-row__gutter">{gutter}</span>}
            <span className="line-row__content">{content}</span>
        </div>
    );
});

LineRow.displayName = 'LineRow';

export default LineRow;
